import {skipToken} from '@reduxjs/toolkit/query';
import {Box, HStack, Image, Space, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {ReactElement} from 'react';
import {useParams} from 'react-router-dom';

import {isNotEmpty, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps, useNavigate, useOnMount} from 'shared/web';

import {LanguageSwitcher} from '../../components/Layout/components/Header/components/LanguageSwitcher';
import {PageBackgroundGradient} from '../../components/PageBackgroundGradient/PageBackgroundGradient';
import i18n from '../../i18n/i18n';
import {useCreateBatchMutation, useGetBatchQuery} from '../../store/importApi';
import {handleApiError} from '../../utils/handleApiError';
import {getImportBatchPath, getImportPath} from '../../utils/routing';
import {Stepper} from '../Create/components/Stepper';
import {ImportPricingView} from './components/ImportPricingView';
import {ImportResultView} from './components/ImportResultView';
import {ImportFormPayload, ImportUploadForm} from './components/ImportUploadForm';
import {isBatchPriceReportRequested} from './utils/isBatchPriceReportRequested';

const IMPORT_STEP_UPLOAD = 0;
const IMPORT_STEP_RESULT = 1;
const IMPORT_STEP_PRICING = 2;

export function ImportPage(props: TestIdProps) {
  const params = useParams<{batchId?: string}>();
  const navigate = useNavigate();
  const [createBatch, {isLoading: isCreateBatchLoading}] = useCreateBatchMutation();
  const {batchId} = params;

  const {data: batch} = useGetBatchQuery(isNotNil(batchId) ? {batchId} : skipToken);

  const importSteps = [
    i18n.t('page.import.labels.stepFileUpload'),
    i18n.t('page.import.labels.stepDataValidation'),
    i18n.t('page.import.labels.stepVehiclePricing'),
  ];

  const activeStep = match<[boolean, boolean], number>([
    isNotNil(batchId),
    isBatchPriceReportRequested(batch?.status),
  ])
    .with([false, Pattern.any], () => IMPORT_STEP_UPLOAD)
    .with([true, true], () => IMPORT_STEP_PRICING)
    .with([true, false], () => IMPORT_STEP_RESULT)
    .exhaustive();

  useOnMount(() => {
    document.getElementById('loading-screen')?.remove();
  });

  const handleStepChange = (step: number) => {
    if (step === IMPORT_STEP_UPLOAD) {
      navigate(getImportPath());
      return;
    }
    if ((step === IMPORT_STEP_RESULT || step === IMPORT_STEP_PRICING) && isNotNil(batchId)) {
      navigate(getImportBatchPath(batchId));
    }
  };

  const handleImport = (payload: ImportFormPayload) => {
    createBatch({
      body: {
        file: payload.file,
        market: payload.market,
        ...(isNotNil(payload.name) && isNotEmpty(payload.name) && {name: payload.name}),
        use_vin_decoding: payload.use_vin_decoding ?? false,
        lang: i18n.language,
      },
    })
      .unwrap()
      .then((response) => {
        navigate(getImportBatchPath(response.id));
      })
      .catch(handleApiError);
  };

  return (
    <>
      <Box backgroundColor="palettes.blue.70.100" paddingHorizontal={6}>
        <HStack height={12} align="center" justify="space-between">
          <Image src="assets/images/logo.svg" maxWidth={125} alt="Logo" />
          <LanguageSwitcher data-testid={suffixTestId('languageSwitcher', props)} />
        </HStack>
      </Box>

      <PageBackgroundGradient data-testid={suffixTestId('background', props)}>
        <VStack shrink={1} maxWidth={315} width="100%" align="center">
          <Stepper
            steps={importSteps}
            value={activeStep}
            onChange={handleStepChange}
            data-testid={suffixTestId('stepper', props)}
          />
          <Space vertical={6} />
          {match<[number, string | undefined], ReactElement>([activeStep, batchId])
            .with([IMPORT_STEP_PRICING, Pattern.string], ([, id]) => (
              <ImportPricingView batchId={id} data-testid={suffixTestId('pricing', props)} />
            ))
            .with([IMPORT_STEP_RESULT, Pattern.string], ([, id]) => (
              <ImportResultView batchId={id} data-testid={suffixTestId('result', props)} />
            ))
            .otherwise(() => (
              <ImportUploadForm
                onImport={handleImport}
                isImporting={isCreateBatchLoading}
                data-testid={suffixTestId('uploadForm', props)}
              />
            ))}
        </VStack>
      </PageBackgroundGradient>
    </>
  );
}
